import { Injectable } from '@angular/core';
import { Observable, tap, catchError, throwError, timer } from 'rxjs';
import { retry, timeout } from 'rxjs/operators';
import { ApiClient } from '@core/http/api-client';
import { UserSessionService } from '@core/services/session/user-session.service';
import { Router } from '@angular/router';
import { Access } from '@core/enums/access';
import { ServiceNotification } from '@shared/services/notification/service-notification';
import { User } from '@core/interfaces/interfaces';
import { InputDTOLogin, LoginResponse, LogoutResponse, RefreshTokenResponse } from './auth.model';

@Injectable({
	providedIn: 'root',
})
export class AuthApi {
	get user$() {
		return this.userSession.user$;
	}

	constructor(
		private api: ApiClient,
		private userSession: UserSessionService,
		private router: Router,
		private serviceNotification: ServiceNotification
	) {}

	login(data: InputDTOLogin): Observable<LoginResponse> {
		return this.api
			.post<LoginResponse>('/auth/signin', data, {
				withCredentials: true,
			})
			.pipe(
				retry({
					count: 2,
					delay: (error) =>
						error?.status === 0 ? timer(600) : throwError(() => error),
				}),
				tap((response: LoginResponse) =>
					this.handleLoginSuccess(response)
				),
				catchError((error) => this.handleLoginError(error))
			);
	}

	private handleLoginSuccess(response: LoginResponse): void {

		if (!response.user) {
			throw new Error('Resposta de login inválida');
		}

		this.userSession.setUser(response.user);

		if (response.user.access === Access.negado) {
			this.handleAccessDenied();
			return;
		}

		this.router.navigate(['/main/welcome']);
	}

	private handleAccessDenied(): void {
		this.clearUserData();
		this.router.navigate(['/login']);
		this.serviceNotification.error(
			'Acesso negado',
			'Acesso negado. Contate o administrador.'
		);
		throw new Error('Acesso negado. Contate o administrador.');
	}

	private handleLoginError(error: any): Observable<never> {
		return throwError(() => error);
	}

	logout(): Observable<LogoutResponse> {
		return this.api
			.post<LogoutResponse>(
				'/auth/signout',
				{},
				{ withCredentials: true }
			)
			.pipe(
				tap(() => this.handleLogoutSuccess()),
				catchError((error) => this.handleLogoutError(error))
			);
	}

	private handleLogoutSuccess(): void {
		this.clearUserData();
		this.router.navigate(['/login']);
	}

	private handleLogoutError(error: any): Observable<never> {

		this.clearUserData();
		this.router.navigate(['/login']);
		return throwError(() => error);
	}

	refreshToken(): Observable<RefreshTokenResponse> {
		return this.api
			.post<RefreshTokenResponse>(
				'/auth/refresh-token',
				{},
				{ withCredentials: true }
			)
			.pipe(
				catchError((error) => this.handleRefreshTokenError(error))
			);
	}

	private handleRefreshTokenError(error: any): Observable<never> {

		this.clearUserData();
		this.router.navigate(['/login']);
		return throwError(() => error);
	}

	checkAuthentication(): Observable<{ authenticated: boolean }> {
		return this.api
			.get<{ authenticated: boolean }>('/auth/validate', {
				withCredentials: true,
			})
			.pipe(
				timeout({ each: 5000 }),
				tap((response) => {
					if (!response.authenticated) {
						this.clearUserData();
					}
				}),
				catchError(() => {
					this.clearUserData();
					return throwError(() => new Error('Not authenticated'));
				})
			);
	}

	getCurrentUser(): User | null {
		return this.userSession.getCurrentUser();
	}

	isLoggedIn(): boolean {
		return this.userSession.isLoggedIn();
	}

	private clearUserData(): void {
		this.userSession.clearUser();
	}
}
